/**
 * PlayModeSnapshot
 *
 * Captures the full scene state when entering play mode and restores it on stop.
 * Tracks transforms, active states, behavior data, parent relationships,
 * and which objects existed — so objects created during play are deleted
 * and objects deleted during play are recreated.
 *
 * `enterPlayMode` / `exitPlayMode` are how the toolbar's Play button and the assistant's `set_play_mode` enter and
 * leave play mode, so nothing play mode changes survives their return to editing. Opening a scene ends play mode
 * with `abandonPlayMode`: there is no edit state of the new scene to restore, but undo thaws.
 */

import type { TraceScene, SceneObjectData } from 'trace-viewer';
import { Keyframes, Properties } from 'trace-viewer/behaviors';
import { getUndoManager } from 'trace-viewer/editor';
import { Quaternion } from '@babylonjs/core/Maths/math.vector';
import { EDITOR_EVENTS } from './editor-events';

interface ObjectSnapshot {
    data: SceneObjectData;
    parentPath: string | null;
}

export class PlayModeSnapshot {
    private snapshot: Map<string, ObjectSnapshot> | null = null;

    /** Whether a snapshot is currently held (i.e. we're in play mode) */
    get isActive(): boolean {
        return this.snapshot !== null;
    }

    /**
     * Capture the current scene state. Call this right before entering play mode.
     */
    capture(traceScene: TraceScene): void {
        const objects = traceScene.getSceneObjects();
        this.snapshot = new Map();

        for (const obj of objects) {
            // refreshSceneData captures transforms + serializes all behaviors
            obj.refreshSceneData();
            const data = obj.getSceneObjectData();
            const parent = obj.getParent();
            const parentPath = parent?.getPath() ?? null;

            this.snapshot.set(obj.getPath(), { data, parentPath });
        }

        console.log(`[PlayModeSnapshot] Captured ${this.snapshot.size} objects`);
    }

    /**
     * Restore the scene to the captured state. Call this when exiting play mode.
     * Returns a promise since object recreation is async.
     */
    async restore(traceScene: TraceScene): Promise<void> {
        if (!this.snapshot) {
            console.warn('[PlayModeSnapshot] No snapshot to restore');
            return;
        }

        const snapshot = this.snapshot;
        this.snapshot = null;

        const currentObjects = traceScene.getSceneObjects();
        const currentPaths = new Set(currentObjects.map(obj => obj.getPath()));
        const snapshotPaths = new Set(snapshot.keys());

        // 1. Delete objects that were created during play (not in snapshot)
        for (const obj of currentObjects) {
            const path = obj.getPath();
            if (!snapshotPaths.has(path)) {
                console.log(`[PlayModeSnapshot] Deleting play-created object: ${path}`);
                traceScene.deleteSceneObject(obj);
            }
        }

        // 2. Recreate objects that were deleted during play (in snapshot but not current)
        //    Recreate in order — parents before children (snapshot preserves insertion order
        //    which matches the topological order from the original scene load).
        for (const [path, entry] of snapshot) {
            if (!currentPaths.has(path)) {
                console.log(`[PlayModeSnapshot] Recreating play-deleted object: ${path}`);
                const parent = entry.parentPath
                    ? traceScene.resolveSceneObject(entry.parentPath) ?? undefined
                    : undefined;
                await traceScene.createSceneObjectFromData(entry.data, parent);
            }
        }

        // 3. Restore state on surviving objects
        for (const [path, entry] of snapshot) {
            if (!currentPaths.has(path)) continue; // was recreated above with correct data

            const obj = traceScene.resolveSceneObject(path);
            if (!obj) continue;

            const data = entry.data;

            // Restore transform directly on the Babylon node (no model reload needed).
            // Skip $name property references — those are resolved by the property system.
            const t = data.transform;
            if (t?.translation && typeof t.translation[0] === 'number') {
                obj.transform.position.set(
                    t.translation[0],
                    t.translation[1],
                    t.translation[2],
                );
            }
            if (t?.rotation && typeof t.rotation[0] === 'number') {
                const r = t.rotation;
                obj.transform.rotationQuaternion = new Quaternion(
                    r[0], r[1], r[2], r[3],
                );
            }
            if (t?.scale && typeof t.scale[0] === 'number') {
                obj.transform.scaling.set(
                    t.scale[0],
                    t.scale[1],
                    t.scale[2],
                );
            }

            // Restore active state — skip $name references (property system handles those). Through the object, not
            // the node: a runtime setActive set the object's own flag, which the node's enabled state alone leaves stale.
            if (typeof data.active === 'boolean') {
                obj.setActive(data.active);
            }

            // Restore behavior data via commit()
            const savedBehaviors = data.behaviors ?? [];
            for (const saved of savedBehaviors) {
                const behavior = obj.getBehavior(saved['@type']);
                if (behavior) {
                    try {
                        await behavior.commit(saved);
                    } catch (err) {
                        console.error(`[PlayModeSnapshot] Failed to restore ${saved['@type']} on ${path}:`, err);
                    }
                }
            }

            // Drop runtime setValue overrides made during play (e.g. AutoUI swatch
            // picks). A commit alone keeps an override whose declaration value is
            // unchanged, so the picked value would otherwise survive the restore.
            obj.getBehavior(Properties)?.clearRuntimeOverrides();

            // Sync sceneData to match restored state
            obj.refreshSceneData();
        }

        console.log(`[PlayModeSnapshot] Restored ${snapshot.size} objects`);
    }
}

/** What entering play mode saved for the scene that plays, until it leaves play mode. Undo is frozen meanwhile. */
interface PlaySession {
    scene: TraceScene;
    snapshot: PlayModeSnapshot;
    /** Each Keyframes container's selection, which the runtime overwrites on play and clears on stop. */
    keyframes: Map<Keyframes, string | null>;
}
let playing: PlaySession | null = null;

/** Enter play mode: snapshot the scene and its keyframe selection, freeze undo, then let the runtime play. */
export function enterPlayMode(traceScene: TraceScene): void {
    if (traceScene.isPlayMode()) return;
    const keyframes = new Map<Keyframes, string | null>();
    for (const obj of traceScene.getSceneObjects()) {
        const kf = obj.getBehavior(Keyframes);
        if (kf) keyframes.set(kf, kf.getActiveKeyframe());
    }
    const snapshot = new PlayModeSnapshot();
    snapshot.capture(traceScene);
    playing = { scene: traceScene, snapshot, keyframes };
    // Frozen first: start events fire inside setPlayMode, and nothing they do is an edit.
    getUndoManager().freeze();
    try {
        traceScene.setPlayMode(true);
    } catch (err) {
        abandonPlayMode();
        throw err;
    }
}

/**
 * Leave play mode: stop the runtime, restore the scene as it was, re-select the keyframes the editor had, and thaw
 * undo, even when the restore throws.
 */
export async function exitPlayMode(traceScene: TraceScene): Promise<void> {
    if (!traceScene.isPlayMode()) return;
    const session = playing?.scene === traceScene ? playing : null;
    playing = null;
    try {
        traceScene.setPlayMode(false);
        if (!session) return;
        await session.snapshot.restore(traceScene);
        for (const [kf, name] of session.keyframes) {
            // null = Base, where the runtime already left it on stop. Re-selecting re-arms keyframe capture deliberately.
            if (name !== null && name !== kf.getActiveKeyframe()) await kf.goTo(name, false);
            // The behaviors panel's card strip follows getActiveKeyframe(); this snaps its highlight now.
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent(EDITOR_EVENTS.KEYFRAME_SELECTION_CHANGED, { detail: { containerPath: kf.sceneObject.getPath() } }));
            }
        }
    } finally {
        getUndoManager().thaw();
    }
}

/**
 * Drop a play session without restoring it, and thaw undo: opening another scene ends play mode with nothing of the
 * old scene to put back.
 */
export function abandonPlayMode(): void {
    if (!playing) return;
    playing = null;
    getUndoManager().thaw();
}
